// W10-MARKET-SIGNALS (D488): the wire types of the incentive-zone console.
export * from './incentive-zones.js';
// D616 (ADR-0121): the wire types of the service-area editor.
export * from './service-areas.js';
// D561 (ADR-0116): the wire types of device pairing; the operator calls are below, the device's own client is `DeviceApi`.
export * from './devices.js';
// D647 (ADR-0123): the wire types of the Painel TV links; the operator calls are below, the TV's own client is `TvPanelApi`.
export * from './tv-panels.js';
// W9-RISK-AREAS-ALERTS (D474): the wire types of the risk-area console, the alert center, the catalog and the individual alert.
export * from './risk-alerts.js';
// W9-OPS-LIVE-MAP (D471): the wire types of the live map, the period dashboard, the route analysis and the anomaly console.
export * from './ops-live-map.js';
// W9-RIDES-REPORTS-AUDIT (D472): the wire types of the ride grid, the batch cancel, the report catalogue and the audit trail.
export * from './ops-reports.js';
/**
 * Why an operator cancels a ride — the closed vocabulary `POST /v1/ops/rides/:id/cancel` accepts (mirrors `@vello/ride`
 * `OPS_CANCEL_REASON_CODES`; W5-HOTFIX, D382). The code is what the ride keeps and what the rider, the driver and the
 * partner desk may read; free text is refused (`400`, `OPS_CANCEL_REASON_UNKNOWN`). `ops_start_pin_locked` (D399) is how
 * support ends a ride whose start PIN locked after five wrong attempts — nobody at fault, no fee.
 */
export const OPS_CANCEL_REASON_CODES = ['ops_rider_request', 'ops_driver_request', 'ops_safety_concern', 'ops_duplicate_request', 'ops_payment_issue', 'ops_start_pin_locked', 'ops_other'];
/** The longest internal note an operator cancel keeps (characters, after trimming). */
export const OPS_CANCEL_NOTE_MAX_LENGTH = 500;
/**
 * Whether the server's audit can keep a note (mirrors `@vello/ride` `isStorableOpsCancelNote`; W5H-01): no control
 * character other than a tab, a line feed or a carriage return, and no unpaired UTF-16 surrogate. The server refuses
 * anything else `400 OPS_CANCEL_NOTE_INVALID` before it cancels.
 */
export function isStorableOpsCancelNote(note) {
    for (let i = 0; i < note.length; i += 1) {
        const c = note.charCodeAt(i);
        if ((c < 0x20 && c !== 0x09 && c !== 0x0a && c !== 0x0d) || c === 0x7f)
            return false;
        if (c >= 0xd800 && c <= 0xdbff) {
            const next = note.charCodeAt(i + 1);
            if (!(next >= 0xdc00 && next <= 0xdfff))
                return false;
            i += 1;
        }
        else if (c >= 0xdc00 && c <= 0xdfff) {
            return false;
        }
    }
    return true;
}
/**
 * D22 NAV-COUNTS (D560): the queues `GET /v1/ops/counts` counts — each one the list its console screen already shows
 * (the server counts it with that list's own permission and read).
 */
export const OPS_COUNT_KEYS = [
    'courier_stuck',
    'safety_incidents',
    'risk_cases',
    'ops_incidents',
    'payouts_pending',
    'partner_payments',
    'compensation_requests',
    'platform_alerts',
    'disputes_open',
    'driver_documents_pending',
];
export class OpsApi {
    client;
    constructor(client) {
        this.client = client;
    }
    /**
     * The waiting-queue counts in one round trip (D560). With `market`: that city's. Without it: country scope (`country`) or platform
     * scope (neither) — a per-city queue is then the SUM over every city the caller may list (owner decision 2026-10-13). A caller
     * allowed to list none of the queues is refused 403.
     */
    counts(market, country) {
        const params = new URLSearchParams();
        if (market !== undefined && market !== '')
            params.set('market', market);
        else if (country !== undefined && country !== '')
            params.set('country', country);
        const qs = params.toString();
        return this.client.request({ method: 'GET', path: `/v1/ops/counts${qs !== '' ? `?${qs}` : ''}`, auth: true });
    }
    /** The scoped live dashboard — KPIs + the active-ride queue (S112). Operator `ops:view`. */
    dashboard(scope = {}) {
        const params = new URLSearchParams();
        if (scope.country !== undefined)
            params.set('country', scope.country);
        if (scope.city !== undefined)
            params.set('city', scope.city);
        const qs = params.toString();
        return this.client.request({ method: 'GET', path: `/v1/ops/dashboard${qs !== '' ? `?${qs}` : ''}`, auth: true });
    }
    /** A ride's ops detail (scope-checked on the server). */
    rideDetail(rideId) {
        return this.client.request({ method: 'GET', path: `/v1/ops/rides/${encodeURIComponent(rideId)}`, auth: true });
    }
    /**
     * Cancel a ride as an operator with a reason code + audit (S112; W5-HOTFIX, D382). Operator `ride:cancel`. Pass the
     * code alone (the pre-D382 call shape, now typed to the vocabulary) or `{ code, note }` — the note is internal.
     */
    cancelRide(rideId, input) {
        const { code, note } = typeof input === 'string' ? { code: input, note: undefined } : input;
        const trimmed = note?.trim();
        return this.client.request({
            method: 'POST',
            path: `/v1/ops/rides/${encodeURIComponent(rideId)}/cancel`,
            body: { code, ...(trimmed !== undefined && trimmed !== '' ? { note: trimmed } : {}) },
            auth: true,
        });
    }
    candidates(rideId, query) {
        const params = new URLSearchParams({
            lat: String(query.latitude),
            lng: String(query.longitude),
            ...(query.radiusMeters !== undefined ? { radius: String(query.radiusMeters) } : {}),
            ...(query.limit !== undefined ? { limit: String(query.limit) } : {}),
        });
        return this.client.request({ method: 'GET', path: `/v1/dispatch/rides/${encodeURIComponent(rideId)}/candidates?${params.toString()}`, auth: true });
    }
    /** Manual dispatch: assign a specific driver (operator AAL2; server records a `manual_dispatch` audit). */
    manualDispatch(rideId, input) {
        return this.client.request({ method: 'POST', path: `/v1/dispatch/rides/${encodeURIComponent(rideId)}/manual`, body: input, auth: true });
    }
    /**
     * Issue a dispatch wave to a set of candidate drivers (operator `dispatch:override`; audited). The server
     * requires a non-empty `candidates` set — an empty set is answered with `noDriver: true` and no offers, so
     * the operator flow is: {@link candidates} to read who is nearby, then `dispatchWave` with the chosen ids.
     */
    dispatchWave(rideId, input) {
        return this.client.request({ method: 'POST', path: `/v1/dispatch/rides/${encodeURIComponent(rideId)}/offers`, body: input, auth: true });
    }
    /** D586 (ADR-0110): the ride's dispatch decision trail, oldest first (`dispatch:view` at the ride's market). */
    dispatchDecisions(rideId) {
        return this.client.request({ method: 'GET', path: `/v1/dispatch/rides/${encodeURIComponent(rideId)}/decisions`, auth: true });
    }
    // ---- W9-OPS-LIVE-MAP (D471, ADR-0089) ----------------------------------------------------------------
    /** The market, the tile source status and the console policy of the live map (`ops:view` at the market). */
    mapConfig(market) {
        return this.client.request({ method: 'GET', path: `/v1/ops/map/config?market=${encodeURIComponent(market)}`, auth: true });
    }
    /** Every driver of a market with its presence, the counts and the rides waiting (`ops:view` at the market). */
    fleet(query) {
        const params = new URLSearchParams({ market: query.market });
        if (query.presence !== undefined && query.presence.length > 0)
            params.set('presence', query.presence.join(','));
        if (query.category !== undefined && query.category !== '')
            params.set('category', query.category);
        if (query.q !== undefined && query.q.trim() !== '')
            params.set('q', query.q.trim());
        return this.client.request({ method: 'GET', path: `/v1/ops/map/fleet?${params.toString()}`, auth: true });
    }
    /** Rides and money over a window, for a market, a state, a country or the platform (`ops:view` at that scope). */
    periodDashboard(query) {
        const params = new URLSearchParams({ from: query.from, to: query.to });
        if (query.market !== undefined)
            params.set('market', query.market);
        else if (query.region !== undefined)
            params.set('region', query.region);
        else if (query.country !== undefined)
            params.set('country', query.country);
        return this.client.request({ method: 'GET', path: `/v1/ops/dashboard/period?${params.toString()}`, auth: true });
    }
    /** A market's today so far, hour by hour, in its own clock, with yesterday's same-elapsed figures (`ops:view` at the market; D559). */
    nowDashboard(market) {
        return this.client.request({ method: 'GET', path: `/v1/ops/dashboard/now?${new URLSearchParams({ market }).toString()}`, auth: true });
    }
    // ---- W9-RIDES-REPORTS-AUDIT (D472, ADR-0090) ----------------------------------------------------------------
    /** The rides of a market over a period, newest first, keyset-paged (`ride:view` at the market). */
    rideGrid(query) {
        const params = new URLSearchParams({ market: query.market, from: query.from, to: query.to });
        if (query.status !== undefined && query.status.length > 0)
            params.set('status', query.status.join(','));
        if (query.category !== undefined && query.category !== '')
            params.set('category', query.category);
        if (query.payment !== undefined && query.payment !== '')
            params.set('payment', query.payment);
        if (query.q !== undefined && query.q.trim() !== '')
            params.set('q', query.q.trim());
        if (query.limit !== undefined)
            params.set('limit', String(query.limit));
        if (query.cursor !== undefined)
            params.set('cursor', query.cursor);
        return this.client.request({ method: 'GET', path: `/v1/ops/rides?${params.toString()}`, auth: true });
    }
    /** Cancel (never delete) up to 50 rides of a market with one reason code; audited first, one outcome per ride (`ride:cancel_batch`). */
    batchCancelRides(input) {
        const note = input.note?.trim();
        return this.client.request({
            method: 'POST',
            path: '/v1/ops/rides/batch-cancel',
            body: { market: input.market, rideIds: [...input.rideIds], code: input.code, ...(note !== undefined && note !== '' ? { note } : {}) },
            auth: true,
        });
    }
    /** The closed catalogue of exportable reports (`report:view`). */
    reportCatalog() {
        return this.client.request({ method: 'GET', path: '/v1/ops/reports', auth: true });
    }
    /** A report's first rows (`report:view` at the market, state or country asked for). Filters are flat query parameters. */
    reportPreview(reportId, input) {
        const params = new URLSearchParams();
        if (input.market !== undefined)
            params.set('market', input.market);
        if (input.region !== undefined)
            params.set('region', input.region);
        if (input.country !== undefined)
            params.set('country', input.country);
        if (input.from !== undefined)
            params.set('from', input.from);
        if (input.to !== undefined)
            params.set('to', input.to);
        for (const [k, v] of Object.entries(input.filters ?? {}))
            params.set(k, Array.isArray(v) ? v.join(',') : String(v));
        return this.client.request({ method: 'GET', path: `/v1/ops/reports/${encodeURIComponent(reportId)}?${params.toString()}`, auth: true });
    }
    /** The report as CSV text (UTF-8 with a BOM) wrapped in JSON; the server audits the export first (`report:export`). */
    reportExport(reportId, input) {
        return this.client.request({
            method: 'POST',
            path: `/v1/ops/reports/${encodeURIComponent(reportId)}/export`,
            body: { ...input, filters: { ...(input.filters ?? {}) }, format: 'json' },
            auth: true,
        });
    }
    /** The global audit trail: a market's, or (no market, a global grant) the platform's (`audit:inspect`). */
    auditTrail(query) {
        const params = new URLSearchParams({ from: query.from, to: query.to });
        if (query.market !== undefined)
            params.set('market', query.market);
        if (query.source !== undefined && query.source.length > 0)
            params.set('source', query.source.join(','));
        if (query.actor !== undefined && query.actor !== '')
            params.set('actor', query.actor);
        if (query.action !== undefined && query.action !== '')
            params.set('action', query.action);
        if (query.limit !== undefined)
            params.set('limit', String(query.limit));
        if (query.cursor !== undefined)
            params.set('cursor', query.cursor);
        return this.client.request({ method: 'GET', path: `/v1/ops/audit?${params.toString()}`, auth: true });
    }
    /** A ride's three lines: to pickup, the quoted estimate, executed (`ride:view` at the ride's market). */
    routeAnalysis(rideId) {
        return this.client.request({ method: 'GET', path: `/v1/ops/rides/${encodeURIComponent(rideId)}/route-analysis`, auth: true });
    }
    /** A market's route-anomaly alerts, newest first (`safety:view` at the market). */
    routeAnomalies(market, statuses = []) {
        const params = new URLSearchParams({ market });
        if (statuses.length > 0)
            params.set('status', statuses.join(','));
        return this.client.request({ method: 'GET', path: `/v1/safety/route-anomalies?${params.toString()}`, auth: true });
    }
    /** One alert with its history of operator transitions (`safety:view` at the market). */
    routeAnomaly(market, alertId) {
        return this.client.request({ method: 'GET', path: `/v1/safety/route-anomalies/${encodeURIComponent(alertId)}?market=${encodeURIComponent(market)}`, auth: true });
    }
    /** Acknowledge, reopen an acknowledgement, resolve or dismiss an alert (`safety:manage` at the market; the actor is the session). */
    actOnRouteAnomaly(market, alertId, input) {
        const note = input.note?.trim();
        return this.client.request({
            method: 'POST',
            path: `/v1/safety/route-anomalies/${encodeURIComponent(alertId)}/actions?market=${encodeURIComponent(market)}`,
            body: { action: input.action, ...(input.code !== undefined ? { code: input.code } : {}), ...(note !== undefined && note !== '' ? { note } : {}), ...(input.revision !== undefined ? { revision: input.revision } : {}) },
            auth: true,
        });
    }
    /** A market's risk areas, every version newest first (`safety:view` at the market). */
    riskAreas(market) {
        return this.client.request({ method: 'GET', path: `/v1/risk-areas?market=${encodeURIComponent(market)}`, auth: true });
    }
    /** Propose a new risk area, or a new version of one (`safety:manage` at the market); another operator approves it. */
    proposeRiskArea(market, input) {
        const { areaId, effectiveTo, schedule, categoryCodes, ...rest } = input;
        return this.client.request({
            method: 'POST',
            path: `/v1/risk-areas?market=${encodeURIComponent(market)}`,
            body: { ...rest, categoryCodes: categoryCodes ?? [], ...(areaId !== undefined ? { areaId } : {}), ...(effectiveTo !== undefined ? { effectiveTo } : {}), ...(schedule !== undefined ? { schedule } : {}) },
            auth: true,
        });
    }
    /** Approve or reject a proposed version (`safety:manage`, by an operator other than its maker; the actor is the session). */
    decideRiskArea(market, areaId, version, input) {
        const reason = input.reason?.trim();
        return this.client.request({
            method: 'POST',
            path: `/v1/risk-areas/${encodeURIComponent(areaId)}/versions/${version}/decision?market=${encodeURIComponent(market)}`,
            body: { decision: input.decision, ...(reason !== undefined && reason !== '' ? { reason } : {}) },
            auth: true,
        });
    }
    /** A market's devices (an operations-room TV, a kiosk), newest first, with who registered each (`device:view` at the market). */
    devices(market) {
        return this.client.request({ method: 'GET', path: `/v1/ops/devices?market=${encodeURIComponent(market)}`, auth: true });
    }
    /** Register a device in a market (`device:manage`); it stays unpaired until its single-use code is redeemed on it. */
    registerDevice(market, input) {
        return this.client.request({ method: 'POST', path: `/v1/ops/devices?market=${encodeURIComponent(market)}`, body: { kind: input.kind, label: input.label }, auth: true });
    }
    /** Issue the single-use pairing code of an unpaired device (`device:manage`); a new code replaces the previous one. The code is returned this once. */
    issueDevicePairingCode(market, deviceId) {
        return this.client.request({ method: 'POST', path: `/v1/ops/devices/${encodeURIComponent(deviceId)}/pairing-code?market=${encodeURIComponent(market)}`, body: {}, auth: true });
    }
    /** Revoke a device with a reason from the closed list (`device:manage`): its token stops working at once and the registration is terminal. */
    revokeDevice(market, deviceId, reason) {
        return this.client.request({ method: 'POST', path: `/v1/ops/devices/${encodeURIComponent(deviceId)}/revoke?market=${encodeURIComponent(market)}`, body: { reason }, auth: true });
    }
    /** "Excluir" a REVOKED device (`device:manage`): a terminal soft delete - it leaves the list, its trail stays. A device that is not revoked is `409 DEVICE_NOT_REVOKED`. */
    deleteDevice(market, deviceId) {
        return this.client.request({ method: 'DELETE', path: `/v1/ops/devices/${encodeURIComponent(deviceId)}?market=${encodeURIComponent(market)}`, auth: true });
    }
    /** A device's audit trail, newest first (`device:view`). */
    deviceEvents(market, deviceId) {
        return this.client.request({ method: 'GET', path: `/v1/ops/devices/${encodeURIComponent(deviceId)}/events?market=${encodeURIComponent(market)}`, auth: true });
    }
    /** D647: the TV panels whose scope the operator reaches (`device:view` at each panel's scope), newest first. */
    tvPanels() {
        return this.client.request({ method: 'GET', path: '/v1/ops/tv-panels', auth: true });
    }
    /** D647: create a panel (`device:manage` at its scope and its home market): registers its `ops_tv` device and returns the pairing code, shown this once. */
    createTvPanel(input) {
        return this.client.request({ method: 'POST', path: '/v1/ops/tv-panels', body: input, auth: true });
    }
    /** D647: change a panel's name, views, rotation or scope (`device:manage` at the old AND the new scope); the TV picks the change up live. */
    updateTvPanel(panelId, input) {
        return this.client.request({ method: 'PUT', path: `/v1/ops/tv-panels/${encodeURIComponent(panelId)}`, body: input, auth: true });
    }
    /** D647: a new pairing code (`device:manage`). Not paired yet: the code is replaced. Already paired: that pairing is revoked (`replaced`) and the panel
     *  gets a new device - the TV on the wall stops until the code is typed on it (or on another TV). The code is returned this once. */
    issueTvPanelPairingCode(panelId) {
        return this.client.request({ method: 'POST', path: `/v1/ops/tv-panels/${encodeURIComponent(panelId)}/pairing-code`, body: {}, auth: true });
    }
    /** D647: revoke a panel with a reason from the closed list (`device:manage`): the TV drops on its next read; terminal. */
    revokeTvPanel(panelId, reason) {
        return this.client.request({ method: 'POST', path: `/v1/ops/tv-panels/${encodeURIComponent(panelId)}/revoke`, body: { reason }, auth: true });
    }
    /** D647: "Excluir" a panel (`device:manage` at its scope): a terminal soft delete. An active panel is revoked first (and its TV stops at once); the panel leaves the list, its trail stays. */
    deleteTvPanel(panelId) {
        return this.client.request({ method: 'DELETE', path: `/v1/ops/tv-panels/${encodeURIComponent(panelId)}`, auth: true });
    }
    /** D647: a panel's audit trail, newest first (`device:view`). */
    tvPanelEvents(panelId) {
        return this.client.request({ method: 'GET', path: `/v1/ops/tv-panels/${encodeURIComponent(panelId)}/events`, auth: true });
    }
    /** A market's incentive zones, every version newest first, and the implicit market-wide zone (`incentive_zone:view` at the market). */
    incentiveZones(market) {
        return this.client.request({ method: 'GET', path: `/v1/incentive-zones?market=${encodeURIComponent(market)}`, auth: true });
    }
    /** Propose a new incentive zone, or a new version of one (`incentive_zone:manage` at the market); another operator approves it. */
    proposeIncentiveZone(market, input) {
        const { zoneId, effectiveTo, ...rest } = input;
        return this.client.request({
            method: 'POST',
            path: `/v1/incentive-zones?market=${encodeURIComponent(market)}`,
            body: { ...rest, ...(zoneId !== undefined ? { zoneId } : {}), ...(effectiveTo !== undefined ? { effectiveTo } : {}) },
            auth: true,
        });
    }
    /** Approve or reject a proposed version (`incentive_zone:manage`, by an operator other than its maker; the actor is the session). */
    decideIncentiveZone(market, zoneId, version, input) {
        const reason = input.reason?.trim();
        return this.client.request({
            method: 'POST',
            path: `/v1/incentive-zones/${encodeURIComponent(zoneId)}/versions/${version}/decision?market=${encodeURIComponent(market)}`,
            body: { decision: input.decision, ...(reason !== undefined && reason !== '' ? { reason } : {}) },
            auth: true,
        });
    }
    /** A market's service area (`service_area:view` at the market): the published versions, what is in force and the open proposal with its findings. */
    serviceAreas(market) {
        return this.client.request({ method: 'GET', path: `/v1/service-areas?market=${encodeURIComponent(market)}`, auth: true });
    }
    /** Save the caller's draft of the market's service area (`service_area:manage`): a drawn `geometry` or an imported `source`, never both. */
    saveServiceAreaDraft(market, input) {
        const { geometry, source, schedule, requestedFrom, reasonCode, note } = input;
        const trimmedNote = note?.trim();
        return this.client.request({
            method: 'PUT',
            path: `/v1/service-areas/draft?market=${encodeURIComponent(market)}`,
            body: {
                ...(geometry !== undefined ? { geometry } : {}),
                ...(source !== undefined ? { source } : {}),
                schedule,
                ...(requestedFrom !== undefined ? { requestedFrom } : {}),
                reasonCode,
                ...(trimmedNote !== undefined && trimmedNote !== '' ? { note: trimmedNote } : {}),
            },
            auth: true,
        });
    }
    /** Send the caller's draft for approval (`service_area:manage`; the author only); another operator decides it. */
    submitServiceArea(market, proposalId) {
        return this.client.request({
            method: 'POST',
            path: `/v1/service-areas/proposals/${encodeURIComponent(proposalId)}/submit?market=${encodeURIComponent(market)}`,
            body: {},
            auth: true,
        });
    }
    /** Withdraw an open proposal, a draft or a submission (`service_area:manage`). */
    withdrawServiceArea(market, proposalId) {
        return this.client.request({
            method: 'POST',
            path: `/v1/service-areas/proposals/${encodeURIComponent(proposalId)}/withdraw?market=${encodeURIComponent(market)}`,
            body: {},
            auth: true,
        });
    }
    /** Approve (and publish) or reject a submitted proposal (`service_area:manage`, by an operator other than its author; the actor is the session). */
    decideServiceArea(market, proposalId, input) {
        const reason = input.reason?.trim();
        return this.client.request({
            method: 'POST',
            path: `/v1/service-areas/proposals/${encodeURIComponent(proposalId)}/decision?market=${encodeURIComponent(market)}`,
            body: { decision: input.decision, ...(reason !== undefined && reason !== '' ? { reason } : {}) },
            auth: true,
        });
    }
    /** The platform's alert center of a market (`alert:view`): the valid alerts, newest first, and the unread count. */
    alertCenter(market, unreadOnly = false) {
        return this.client.request({ method: 'GET', path: `/v1/alerts?market=${encodeURIComponent(market)}${unreadOnly ? '&unread=1' : ''}`, auth: true });
    }
    /** Mark an alert of the center read (`alert:manage`; the first reader stays). */
    markAlertRead(market, alertId) {
        return this.client.request({ method: 'POST', path: `/v1/alerts/${encodeURIComponent(alertId)}/read?market=${encodeURIComponent(market)}`, body: {}, auth: true });
    }
    /** The alert catalog and the thresholds in force for a market (`alert:view`). */
    alertCatalog(market) {
        return this.client.request({ method: 'GET', path: `/v1/alerts/catalog?market=${encodeURIComponent(market)}`, auth: true });
    }
    /** Write an individual alert to one rider or driver, with a start and an expiry (`notification:send`, platform). */
    createIndividualAlert(input) {
        return this.client.request({ method: 'POST', path: '/v1/notifications/alerts', body: input, auth: true });
    }
    /** A user's individual alerts that are valid now, with the read flag (`notification:send`). */
    individualAlerts(userId) {
        return this.client.request({ method: 'GET', path: `/v1/notifications/alerts?userId=${encodeURIComponent(userId)}`, auth: true });
    }
    /**
     * D443 (D-31): a market's ratings — the drivers as riders rated them (`drivers`, word `driver:view`) or the riders as
     * drivers rated them (`riders`, `rider:view`) — lowest average first, each over their newest ratings. GEO against the
     * market asked for.
     */
    ratings(who, query) {
        const params = new URLSearchParams({ market: query.market, ...(query.limit !== undefined ? { limit: String(query.limit) } : {}) });
        return this.client.request({ method: 'GET', path: `/v1/ops/ratings/${who}?${params.toString()}`, auth: true });
    }
}
//# sourceMappingURL=ops.js.map